import React, { FC, Fragment, useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Box,
  Stack,
  Typography,
  TextField,
  Autocomplete,
  createFilterOptions,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import EmailIcon from '@mui/icons-material/Email';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { format, parseISO } from 'date-fns';
import { useSnackbar } from 'notistack';
import api, {
  clientApi,
  clientInvoiceApi,
  paymentMethodsApi,
  Client,
  OpenInvoiceHorse,
  OpenInvoiceRow,
  ClientInvoiceRecord,
} from '../utils/api';
import { formatDollars } from '../utils/money';
import { sendErrorMessage } from '../utils/sendErrorMessage';
import {
  recordablePaymentTypeOptions,
  defaultRecordablePaymentType,
  paymentTypesLoadState,
} from '../utils/paymentTypes';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import SendPaymentLinkDialog from './SendPaymentLinkDialog';
import { formatQuantity } from '../utils/quantity';

const STEPS = ['Select Invoices', 'Review Statement', 'Collect Payment', 'Confirm'];

interface Props {
  open: boolean;
  onClose: () => void;
  // When set, open an EXISTING statement to view / pay / print / email it
  // (jumps straight to Collect Payment, or Confirm if already paid) instead of
  // the create flow.
  manageStatementId?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const money = (n: number) => formatDollars(Number(n));

type SelectedInvoice = OpenInvoiceRow & {
  horseId: string;
  horseName: string;
  ownershipPercentage: number | null;
};

const METHOD_LABELS: Record<string, string> = {
  card: 'Credit / Debit Card',
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  card_on_file: 'Card on file',
  cash: 'Cash',
  check: 'Check',
  payment_link: 'Send payment link',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'MMM d yyyy');
  } catch {
    return iso;
  }
}

// ── Client filter ─────────────────────────────────────────────────────────────

const clientFilter = createFilterOptions<Client>({
  stringify: (c) => `${c.name} ${c.email ?? ''} ${c.phone ?? ''}`,
});

// ── Stripe new-card form ──────────────────────────────────────────────────────
// Mirrors the invoice dialog's CardPaymentForm: collects a new card/bank via
// Stripe Elements and confirms the statement PaymentIntent. The statement webhook
// settles the source invoices by clientInvoiceId.
const StatementCardForm: FC<{
  amountLabel: string;
  onSuccess: (processing: boolean) => void;
  onCancel: () => void;
}> = ({ amountLabel, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Please check the card details.');
        setProcessing(false);
        return;
      }
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (confirmError) {
        setError(confirmError.message || 'Payment failed.');
        setProcessing(false);
        return;
      }
      if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
        onSuccess(paymentIntent.status === 'processing');
      } else {
        setError('Payment was not completed. Please try again.');
        setProcessing(false);
      }
    } catch {
      setError('Something went wrong charging the card.');
      setProcessing(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs', wallets: { applePay: 'never', googlePay: 'never' } }} />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button onClick={onCancel} disabled={processing}>Cancel</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || processing}
          sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6B3410' } }}
        >
          {processing ? <CircularProgress size={16} color="inherit" /> : `Charge ${amountLabel}`}
        </Button>
      </Box>
    </form>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const ClientInvoiceWizard: FC<Props> = ({ open, onClose, manageStatementId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const manageMode = !!manageStatementId;

  // ── Wizard state ──────────────────────────────────────────────────────────
  const [step, setStep] = useState(0);

  // Step 1
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<SelectedInvoice[]>([]);

  // Step 2
  const [clientInvoice, setClientInvoice] = useState<ClientInvoiceRecord | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  // A draft's identity: the invoice SET it was created for. Sorted, so
  // re-ticking the same picks in another order is the same key.
  const selectedIdsKey = useMemo(
    () => selectedInvoices.map((i) => i.id).sort().join(','),
    [selectedInvoices],
  );
  // Every draft minted in this dialog, BY that key — a map, not one slot. A
  // single "last key" only recognises the most recent set, so the real
  // change-my-mind walk (tick A → Review → add B → Review → drop B → Review)
  // still minted a third statement over A.
  const [drafts, setDrafts] = useState<Record<string, ClientInvoiceRecord>>({});
  // The CURRENT selection, readable from a promise that resolves later. The
  // creation effect closes over the `selectedIdsKey` of the render that STARTED
  // the create, so comparing that to the same binding inside `.then` compares a
  // value with itself — always true, i.e. no guard at all. Updated in an effect
  // rather than during render, so the ref is written after commit.
  const selectedIdsKeyRef = useRef(selectedIdsKey);
  useEffect(() => {
    selectedIdsKeyRef.current = selectedIdsKey;
  }, [selectedIdsKey]);

  // Step 3
  // `null` = the user has not picked a method yet, so the select follows
  // `defaultRecordablePaymentType` over the CURRENT option list (see
  // `paymentMethod` below). A hardcoded 'cash' seed would preselect a method a
  // clinic had deactivated in Settings, which assertPaymentTypeAllowed 400s.
  const [methodChoice, setMethodChoice] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [selectedCardId, setSelectedCardId] = useState<string>('');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  // The Send Payment Link window; once it has sent, its Done closes the wizard too.
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  // Stripe card flow (credit/debit card via Stripe, like the invoice dialog)
  const [stripeMode, setStripeMode] = useState<'new_card' | 'on_file' | null>(null);
  const [stripeClientSecret, setStripeClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
  const [cardLoading, setCardLoading] = useState(false);

  // ── Reset on close ────────────────────────────────────────────────────────
  const resetAll = useCallback(() => {
    setStep(0);
    setSelectedClient(null);
    setSelectedInvoices([]);
    setClientInvoice(null);
    setDrafts({});
    setCreateError(null);
    setCreating(false);
    setEmailSending(false);
    setMethodChoice(null);
    setPaymentAmount('');
    setPaymentReference('');
    setSelectedCardId('');
    setPaymentError(null);
    setLinkDialogOpen(false);
    setLinkSent(false);
    setConfirmingPayment(false);
    setStripeMode(null);
    setStripeClientSecret(null);
    setStripePromise(null);
    setCardLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    resetAll();
    onClose();
  }, [resetAll, onClose]);

  // Manage mode: load the existing statement and jump to Collect Payment (or
  // Confirm if it's already paid) so it can be viewed / paid / printed / emailed.
  // Superseded statements land on Confirm too — every collect path rejects them
  // server-side, so offering payment fields would only dead-end the user.
  useEffect(() => {
    if (!open || !manageStatementId) return;
    let cancelled = false;
    (async () => {
      try {
        const ci = await clientInvoiceApi.get(manageStatementId);
        if (cancelled) return;
        setClientInvoice(ci);
        setSelectedClient({ id: ci.client.id, name: ci.client.name, email: ci.client.email } as Client);
        setStep(ci.status === 'paid' || ci.status === 'superseded' ? 3 : 2);
      } catch (e: any) {
        if (cancelled) return;
        enqueueSnackbar(e?.response?.data?.message ?? 'Failed to load the statement.', { variant: 'error' });
        onClose();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, manageStatementId]); // eslint-disable-line

  // ── Queries ───────────────────────────────────────────────────────────────

  // All clients for Autocomplete
  const { data: allClients = [] } = useQuery(['clients-list-all'], () =>
    clientApi.list(),
  );

  // Open invoices for selected client
  const {
    data: openInvoiceData,
    isLoading: loadingOpenInvoices,
    error: openInvoicesError,
  } = useQuery(
    ['open-invoices', selectedClient?.id],
    () => clientInvoiceApi.getOpenInvoices(selectedClient!.id),
    { enabled: !!selectedClient },
  );

  // Saved cards for selected client (Step 3)
  const { data: savedCards = [] } = useQuery(
    ['payment-methods', selectedClient?.id],
    () => paymentMethodsApi.list(selectedClient!.id),
    { enabled: !!selectedClient && step === 2 },
  );

  // Clinic-configured payment types (Credit Card, Debit Card, etc.) — same source
  // the invoice payment dialog uses.
  const clinicPaymentTypesQuery = useQuery<Array<{ value: string; label: string }>>(
    ['payment-types'],
    () => api.get('/payment-types').then((r) => r.data),
    { enabled: step === 2 },
  );
  const clinicPaymentTypes = clinicPaymentTypesQuery.data ?? [];
  const paymentTypesState = paymentTypesLoadState(clinicPaymentTypesQuery);

  // Whether the clinic can charge cards via Stripe (same source as the invoice dialog).
  const { data: stripeStatus } = useQuery<{ connected: boolean; chargesEnabled: boolean }>(
    ['stripe-connect-status'],
    () => api.get('/stripe-connect/status').then((r) => r.data),
    { enabled: step === 2 },
  );
  const stripeConnectEnabled = !!(stripeStatus?.connected && stripeStatus?.chargesEnabled);

  // ── Derived ───────────────────────────────────────────────────────────────

  const selectedTotal = useMemo(
    () => selectedInvoices.reduce((sum, inv) => sum + Number(inv.clientOwes), 0),
    [selectedInvoices],
  );

  const invoiceTotal = clientInvoice ? Number(clientInvoice.totalAmount) : selectedTotal;

  const defaultCard = savedCards.find((c) => c.isDefault) ?? savedCards[0] ?? null;

  const formatCard = (c: typeof savedCards[number]) =>
    `${c.cardBrand ? c.cardBrand.charAt(0).toUpperCase() + c.cardBrand.slice(1) : 'Card'} ···${c.cardLast4}`;

  // Method dropdown options: clinic payment types (Cash/Check/Credit Card/Debit
  // Card/...) + Send payment link — mirrors the invoice payment dialog. Credit/
  // Debit Card charge via Stripe when Connect is set up (New Card / On File),
  // else record manually; payment_link opens the Send Payment Link window; the rest record.
  // Same helper the Invoicing screen's selects use, so both screens offer the
  // clinic's own types (including custom ones like "Venmo"). 'card' /
  // 'card_on_file' are this wizard's internal Stripe sub-choices, never methods
  // to record against.
  const recordableMethods = useMemo(
    () =>
      recordablePaymentTypeOptions(clinicPaymentTypes, paymentTypesState).filter(
        (t) => t.value !== 'card' && t.value !== 'card_on_file',
      ),
    [clinicPaymentTypes, paymentTypesState],
  );
  // Loaded, and the clinic has no active payment type at all. The built-in
  // fallback would offer cash/check/card — every one of which the server's
  // assertPaymentTypeAllowed rejects — so offer nothing and block the record.
  const noRecordableMethods = recordableMethods.length === 0;
  const methodOptions = useMemo(
    () => [...recordableMethods, { value: 'payment_link', label: 'Send payment link' }],
    [recordableMethods],
  );

  // The starting selection comes from the SAME helper the Invoicing screen
  // uses, over this wizard's own option list: Cash while the clinic offers it,
  // else the first recordable option. 'Send payment link' is a flow, not a
  // clinic type, so it is never a default candidate. A choice the clinic no
  // longer offers - picked from STATIC_PAYMENT_TYPES while GET /payment-types
  // was still in flight - snaps back to that default the moment the real list
  // lands, instead of rendering blank and 400ing on save.
  // '' when the clinic offers nothing recordable: the select renders empty
  // rather than showing a Cash the server would reject, and canSubmit below
  // stays false until a real method is picked (the BulkMarkPaid pattern).
  const defaultMethod = useMemo(
    () => (noRecordableMethods ? '' : defaultRecordablePaymentType(recordableMethods)),
    [recordableMethods, noRecordableMethods],
  );
  const paymentMethod =
    methodChoice && methodOptions.some((o) => o.value === methodChoice)
      ? methodChoice
      : defaultMethod;

  const methodLabel = (value: string) =>
    clinicPaymentTypes.find((t) => t.value === value)?.label ?? METHOD_LABELS[value] ?? value;

  // Credit/Debit card → charge via Stripe (New Card / On File) when Connect is
  // enabled, exactly like a regular invoice; otherwise a manual record.
  const isCardType = paymentMethod === 'credit_card' || paymentMethod === 'debit_card';
  const useStripeFlow = isCardType && stripeConnectEnabled;

  // Default the saved-card selection + pre-fill the cash/check amount with the
  // full balance when the user reaches the Collect Payment step.
  useEffect(() => {
    if (step === 2 && defaultCard && !selectedCardId) setSelectedCardId(defaultCard.id);
  }, [step, defaultCard]); // eslint-disable-line
  useEffect(() => {
    if (step === 2 && clientInvoice && !paymentAmount) {
      setPaymentAmount(Number(clientInvoice.totalAmount).toFixed(2));
    }
  }, [step, clientInvoice]); // eslint-disable-line

  // Reset the Stripe card sub-flow whenever we leave a card method.
  useEffect(() => {
    if (!useStripeFlow) {
      setStripeMode(null);
      setStripeClientSecret(null);
      setStripePromise(null);
    }
  }, [useStripeFlow]);
  // When a card method is picked, default to the saved card if the client has one
  // (so "On File" is immediately visible), otherwise the new-card form.
  useEffect(() => {
    if (useStripeFlow && stripeMode === null) {
      setStripeMode(savedCards.length > 0 ? 'on_file' : 'new_card');
    }
  }, [useStripeFlow, savedCards.length]); // eslint-disable-line
  // Create the statement PaymentIntent when the user chooses "New Card".
  useEffect(() => {
    if (stripeMode === 'new_card' && useStripeFlow && !stripeClientSecret && !cardLoading) {
      initializeStatementCard();
    }
  }, [stripeMode, useStripeFlow]); // eslint-disable-line

  // ── Grouped invoices for display ──────────────────────────────────────────

  const horseGroups: OpenInvoiceHorse[] = openInvoiceData?.horses ?? [];

  // ── Step 1 helpers ────────────────────────────────────────────────────────

  function isInvoiceSelected(inv: OpenInvoiceRow) {
    return selectedInvoices.some((s) => s.id === inv.id);
  }

  function toggleInvoice(inv: OpenInvoiceRow, horseGroup: OpenInvoiceHorse) {
    setSelectedInvoices((prev) => {
      if (prev.some((s) => s.id === inv.id)) {
        return prev.filter((s) => s.id !== inv.id);
      }
      return [
        ...prev,
        {
          ...inv,
          horseId: horseGroup.horse.id,
          horseName: horseGroup.horse.name,
          ownershipPercentage: horseGroup.ownershipPercentage,
        },
      ];
    });
  }

  function isAllHorseSelected(horseGroup: OpenInvoiceHorse) {
    return horseGroup.invoices.every((inv) => selectedInvoices.some((s) => s.id === inv.id));
  }

  function toggleAllHorse(horseGroup: OpenInvoiceHorse) {
    const allSelected = isAllHorseSelected(horseGroup);
    if (allSelected) {
      setSelectedInvoices((prev) =>
        prev.filter((s) => s.horseId !== horseGroup.horse.id),
      );
    } else {
      const toAdd = horseGroup.invoices
        .filter((inv) => !selectedInvoices.some((s) => s.id === inv.id))
        .map((inv) => ({
          ...inv,
          horseId: horseGroup.horse.id,
          horseName: horseGroup.horse.name,
          ownershipPercentage: horseGroup.ownershipPercentage,
        }));
      setSelectedInvoices((prev) => [...prev, ...toAdd]);
    }
  }

  // ── Step 2: create the statement draft on arrival ─────────────────────────
  // ONE draft per invoice SELECTION, not one per visit. handleBack used to null
  // `clientInvoice`, so Review → Back → Review minted a second CINV-numbered
  // statement over the same invoices (S5 ROOT 3 — "so many statements"). Drafts
  // are now kept per sorted id set: a set we already minted a statement for is
  // shown again — including a set the user comes BACK to after trying another
  // one — and only a set we have never seen mints a new one (so a statement is
  // never numbered for invoices the user deselected). Since A6c the SERVER reuses
  // a manual draft over the same set too (billing.service.createClientInvoice),
  // so closing this dialog and re-selecting the same invoices no longer mints a
  // second statement either; this map stays because it also saves the round trip.
  useEffect(() => {
    if (step !== 1 || creating || !selectedClient) return;

    const existing = drafts[selectedIdsKey];
    if (existing) {
      // Same set as a draft we already have: show that one. The id check keeps
      // a fresher copy (a payment refetch) from being rolled back to the
      // creation snapshot when the user steps back into the preview. Any
      // createError belongs to a create for a DIFFERENT set, so it would sit as
      // a banner over a statement it does not describe.
      if (clientInvoice?.id !== existing.id) setClientInvoice(existing);
      setCreateError(null);
      return;
    }

    // A set we have no draft for, so whatever is in state belongs to the
    // PREVIOUS selection and is stale from here on. Drop it before the call:
    // Step 2's Email to Client / Collect Payment are `disabled={!ci}` and act
    // on `clientInvoice.id`, so a REJECTED create would otherwise leave the
    // previous statement on screen, emailable and payable, under an error
    // banner about the new one.
    if (clientInvoice) setClientInvoice(null);

    const keyForThisDraft = selectedIdsKey;
    setCreating(true);
    setCreateError(null);
    clientInvoiceApi
      .create(
        selectedClient.id,
        selectedInvoices.map((i) => i.id),
      )
      .then((ci) => {
        // Only adopt it if the selection is STILL the one it was created for.
        // The draft itself is always filed under its own key, so a set the user
        // comes back to still finds it; what must not happen is a statement for
        // {A} becoming the statement on screen over {A,B}'s rows (it would be
        // emailable and payable from there — both buttons are `disabled={!ci}`
        // and act on `clientInvoice.id`). Against the REF, never against
        // `selectedIdsKey`: that binding is this render's, the same value
        // `keyForThisDraft` copied, so the comparison would always be true.
        if (keyForThisDraft === selectedIdsKeyRef.current) setClientInvoice(ci);
        setDrafts((prev) => ({ ...prev, [keyForThisDraft]: ci }));
      })
      .catch((err) => {
        // Same ref check, same reason: a failure that describes a set the user is
        // no longer looking at must not put a banner over a statement that saved
        // fine. There is nothing to act on either — that set is not in `drafts`,
        // so returning to it mints, and reports, afresh.
        if (keyForThisDraft !== selectedIdsKeyRef.current) return;
        const msg =
          err?.response?.data?.message ?? err?.message ?? 'Failed to create statement draft.';
        setCreateError(msg);
      })
      // NOT key-checked, deliberately: `creating` belongs to whichever create is
      // on screen, and skipping this would leave a spinner nothing can clear if
      // the selection ever moves without `resetAll` (it cannot today — the X is
      // the only path and it clears the flag itself).
      .finally(() => setCreating(false));
    // ⚠ `creating` is a GUARD, never a DEP, and that is deliberate (2026-09-10,
    // ratified in the review round; see Docs/planning/inconsistency-audit.md).
    // Making it a dep re-runs this effect when a create SETTLES — and after a
    // REJECTED create there is still no draft for the key, so the re-run mints
    // again: an unbounded retry loop against the server, with the error banner
    // cleared by each attempt. The two rejected-create tests in
    // `ClientInvoiceWizard.draft.test.tsx` are the pin; they go red the moment
    // `creating` is added here.
    //
    // The race the dep was proposed for — the selection changing while a create
    // is in flight — is closed in two places instead. Back is DISABLED while
    // `creating` (step 0 is the only place the selection can change, so that is
    // the whole of the Back → re-tick path), and the selection can still move
    // through the title-bar X, which is deliberately NOT disabled (walking away
    // from a hanging save must always be possible) and resets the wizard: the
    // resolved-key check above is what stops that stranded draft from being
    // adopted by the next session, and it has its own test. Back and forward over
    // Review is still the retry gesture for a failed create.
  }, [step, selectedIdsKey]); // eslint-disable-line

  // ── Step 2: email ─────────────────────────────────────────────────────────

  async function handleEmailInvoice() {
    if (!clientInvoice) return;
    setEmailSending(true);
    try {
      await clientInvoiceApi.email(clientInvoice.id, selectedClient?.email);
      enqueueSnackbar(`Statement emailed to ${selectedClient?.email || 'the client'}.`, { variant: 'success' });
    } catch (err: any) {
      // See InvoiceDetail: the server message was already read here, the
      // no-response TIMEOUT case was not — and "Failed" over a send that may
      // have gone out is what makes a user resend and double-email the client.
      const feedback = sendErrorMessage(err, 'Could not email the statement.');
      enqueueSnackbar(feedback.message, { variant: feedback.variant });
    } finally {
      setEmailSending(false);
    }
  }

  // ── Step 3: collect payment ───────────────────────────────────────────────
  // One payment, one method (mirrors the invoice payment dialog). Card-on-file
  // and payment-link settle the whole balance via Stripe; cash/check record a
  // single (optionally partial) manual payment.

  async function handleSubmitPayment() {
    if (!clientInvoice) return;
    setPaymentError(null);
    // A link is made and sent from its own window, the same one a single invoice uses.
    if (paymentMethod === 'payment_link') {
      setLinkDialogOpen(true);
      return;
    }
    setConfirmingPayment(true);
    try {
      if (useStripeFlow && stripeMode === 'on_file') {
        const cardId = selectedCardId || defaultCard?.id;
        if (!cardId) {
          setPaymentError('Select a saved card to charge.');
          return;
        }
        const result = await clientInvoiceApi.charge(clientInvoice.id, cardId);
        if (result.status === 'requires_action') {
          setPaymentError('This card needs extra authentication — use a new card or a payment link.');
          return;
        }
        setClientInvoice(await clientInvoiceApi.get(clientInvoice.id));
        if (result.status === 'processing') {
          enqueueSnackbar('Bank payment is processing — it settles in 1–4 business days.', { variant: 'info' });
        }
        setStep(3);
      } else {
        // Manual record: cash / check / other clinic types — or credit/debit card
        // when Stripe isn't set up (records that a card was paid externally).
        const amt = parseFloat(paymentAmount);
        if (isNaN(amt) || amt <= 0) {
          setPaymentError('Enter a valid amount.');
          return;
        }
        if (amt > invoiceTotal + 0.005) {
          setPaymentError('Amount exceeds the balance due.');
          return;
        }
        const referenceNote =
          paymentMethod === 'check' && paymentReference
            ? `Check #${paymentReference}`
            : paymentReference || undefined;
        const updated = await clientInvoiceApi.recordPayments(clientInvoice.id, [
          { method: paymentMethod, amount: amt, referenceNote },
        ]);
        setClientInvoice(updated);
        setStep(3);
      }
    } catch (err: any) {
      setPaymentError(err?.response?.data?.message ?? err?.message ?? 'Failed to record payment.');
    } finally {
      setConfirmingPayment(false);
    }
  }

  // Start the Stripe new-card flow: create the statement PaymentIntent + load
  // Stripe for the connected account (mirrors the invoice dialog).
  async function initializeStatementCard() {
    if (!clientInvoice || stripeClientSecret || cardLoading) return;
    setCardLoading(true);
    setPaymentError(null);
    try {
      const pubKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (!pubKey) throw new Error('Stripe is not configured.');
      const { clientSecret, stripeAccountId } = await clientInvoiceApi.createPaymentIntent(clientInvoice.id, paymentMethod);
      setStripeClientSecret(clientSecret);
      setStripePromise(loadStripe(pubKey, { stripeAccount: stripeAccountId }));
    } catch (err: any) {
      setPaymentError(err?.response?.data?.message ?? 'Failed to start the card payment.');
      setStripeMode(null);
    } finally {
      setCardLoading(false);
    }
  }

  async function handleCardSuccess(processing: boolean) {
    if (!clientInvoice) return;
    setClientInvoice(await clientInvoiceApi.get(clientInvoice.id));
    enqueueSnackbar(
      processing ? 'Bank payment is processing — it settles in 1–4 business days.' : 'Payment received.',
      { variant: processing ? 'info' : 'success' },
    );
    setStep(3);
  }

  // ── Step 4: done ─────────────────────────────────────────────────────────

  function handleDone() {
    queryClient.invalidateQueries(['invoices']);
    queryClient.invalidateQueries(['billing-stats']);
    queryClient.invalidateQueries(['client-invoices']);
    handleClose();
  }

  async function handleEmailReceipt() {
    if (!clientInvoice) return;
    try {
      await clientInvoiceApi.email(clientInvoice.id, selectedClient?.email);
      enqueueSnackbar(`Statement emailed to ${selectedClient?.email || 'the client'}.`, { variant: 'success' });
    } catch (err: any) {
      // See InvoiceDetail: the server message was already read here, the
      // no-response TIMEOUT case was not — and "Failed" over a send that may
      // have gone out is what makes a user resend and double-email the client.
      const feedback = sendErrorMessage(err, 'Could not email the statement.');
      enqueueSnackbar(feedback.message, { variant: feedback.variant });
    }
  }

  // Print the branded, server-rendered statement PDF (mirrors InvoiceDetail).
  async function handlePrintStatement() {
    if (!clientInvoice) return;
    try {
      const pdfBlob = await clientInvoiceApi.getPdf(clientInvoice.id);
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
          }, 500);
        };
      } else {
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
      }
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message ?? 'Failed to generate statement PDF.', { variant: 'error' });
    }
  }

  // ── Grouped invoices for Step 2 & 4 ──────────────────────────────────────

  const selectedGroupedByHorse = useMemo(() => {
    const map = new Map<string, { horseName: string; ownershipPercentage: number | null; invoices: SelectedInvoice[] }>();
    for (const inv of selectedInvoices) {
      if (!map.has(inv.horseId)) {
        map.set(inv.horseId, {
          horseName: inv.horseName,
          ownershipPercentage: inv.ownershipPercentage,
          invoices: [],
        });
      }
      map.get(inv.horseId)!.invoices.push(inv);
    }
    return Array.from(map.values());
  }, [selectedInvoices]);

  // ── Render helpers ────────────────────────────────────────────────────────

  const stepContent = () => {
    switch (step) {
      case 0:
        return Step1();
      case 1:
        return Step2();
      case 2:
        return Step3();
      case 3:
        return Step4();
      default:
        return null;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 1 — Select Invoices
  // ─────────────────────────────────────────────────────────────────────────

  function Step1(): JSX.Element {
    const totalHorseInvoices = horseGroups.reduce(
      (sum, hg) => sum + hg.invoices.length,
      0,
    );
    const hasNoInvoices =
      !loadingOpenInvoices && selectedClient && totalHorseInvoices === 0;

    return (
      // @ts-expect-error TS2746: TS 4.9 + @types/react 18.3 multiple-children false positive
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 80 }}>
        {/* Client Autocomplete */}
        <Autocomplete
          options={allClients as Client[]}
          getOptionLabel={(c) => c.name}
          filterOptions={clientFilter}
          value={selectedClient}
          onChange={(_, val) => {
            setSelectedClient(val);
            setSelectedInvoices([]);
          }}
          renderOption={(props, c) => (
            <li {...props} key={c.id}>
              <div>
                <Typography variant="body2" fontWeight={600}>
                  {c.name}
                </Typography>
                {(c.email || c.phone) && (
                  <Typography variant="caption" color="text.secondary">
                    {[c.email, c.phone].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </div>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Client" placeholder="Search by name, email, or phone" />
          )}
        />

        {/* Loading spinner */}
        {loadingOpenInvoices && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* API error */}
        {openInvoicesError && (
          <Alert severity="error">
            Failed to load open invoices. Please try again.
          </Alert>
        )}

        {/* Empty state */}
        {hasNoInvoices && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No open invoices found for {selectedClient!.name}.
            </Typography>
          </Box>
        )}

        {/* Invoice table grouped by horse */}
        {horseGroups.map((hg) => {
          const allSelected = isAllHorseSelected(hg);
          const someSelected = hg.invoices.some((inv) =>
            selectedInvoices.some((s) => s.id === inv.id),
          );
          return (
            <Paper key={hg.horse.id} variant="outlined" sx={{ overflow: 'hidden' }}>
              {/* Horse header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  bgcolor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={() => toggleAllHorse(hg)}
                />
                <Typography variant="subtitle2" fontWeight={700}>
                  {hg.horse.name}
                </Typography>
                {hg.ownershipPercentage != null && (
                  <Chip
                    label={`${hg.ownershipPercentage}% ownership`}
                    size="small"
                    sx={{ ml: 'auto', bgcolor: '#8B4513', color: '#fff', fontSize: 11 }}
                  />
                )}
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Billed Total</TableCell>
                      <TableCell align="right">Client Owes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hg.invoices.map((inv) => (
                      <TableRow
                        key={inv.id}
                        hover
                        onClick={() => toggleInvoice(inv, hg)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isInvoiceSelected(inv)}
                            onChange={() => toggleInvoice(inv, hg)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {inv.invoiceNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(inv.issuedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {inv.description ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {money(inv.billedTotal)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color="primary.main"
                          >
                            {money(inv.clientOwes)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          );
        })}

        {/* Sticky footer */}
        {selectedClient && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              px: 3,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 2,
              mx: -3,
              mb: -2,
              zIndex: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {selectedInvoices.length > 0
                ? `${selectedInvoices.length} invoice${selectedInvoices.length !== 1 ? 's' : ''} selected · ${money(selectedTotal)} total`
                : 'No invoices selected'}
            </Typography>
            <Button
              variant="contained"
              disabled={selectedInvoices.length === 0}
              onClick={() => setStep(1)}
              sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6B3410' } }}
            >
              Review Statement →
            </Button>
          </Box>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 2 — Invoice Preview
  // ─────────────────────────────────────────────────────────────────────────

  function Step2() {
    const ci = clientInvoice;
    const total = ci ? Number(ci.totalAmount) : selectedTotal;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Loading / error creating draft */}
        {creating && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
            <Typography sx={{ ml: 2 }} color="text.secondary">
              Saving statement draft…
            </Typography>
          </Box>
        )}
        {createError && (
          <Alert severity="error">{createError}</Alert>
        )}

        {!creating && (
          <>
            {/* Invoice document */}
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                borderColor: 'grey.300',
                fontFamily: '"Inter", sans-serif',
              }}
            >
              {/* Document header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 3,
                }}
              >
                <Box>
                  <Typography variant="h6" fontWeight={700} color="#8B4513">
                    StableTrack Practice
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Equine Veterinary Services
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="overline"
                    display="block"
                    letterSpacing={3}
                    color="#8B4513"
                    fontWeight={700}
                  >
                    STATEMENT
                  </Typography>
                  {ci && (
                    <Typography variant="body2" fontFamily="monospace">
                      {ci.invoiceNumber}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(), 'MMM d, yyyy')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Bill To */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={0.5}
                >
                  Bill To
                </Typography>
                <Typography fontWeight={600}>
                  {selectedClient?.name}
                </Typography>
                {selectedClient?.email && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedClient.email}
                  </Typography>
                )}
              </Box>

              {/* Line items grouped by horse */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Billed Total</TableCell>
                      <TableCell align="right">Client Owes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedGroupedByHorse.map((group) => {
                      const horseSubtotal = group.invoices.reduce(
                        (sum, inv) => sum + Number(inv.clientOwes),
                        0,
                      );
                      return (
                        <Fragment key={group.horseName}>
                        {/* Horse header row */}
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            sx={{
                              bgcolor: '#FAF3EE',
                              py: 0.75,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={700}>
                                {group.horseName}
                              </Typography>
                              {group.ownershipPercentage != null && (
                                <Chip
                                  label={`${group.ownershipPercentage}% ownership`}
                                  size="small"
                                  sx={{
                                    bgcolor: '#CD853F',
                                    color: '#fff',
                                    fontSize: 10,
                                    height: 18,
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                        {/* Invoice rows — each invoice expands to its line items */}
                        {group.invoices.map((inv) => (
                          <Fragment key={inv.id}>
                            <TableRow>
                              <TableCell>
                                <Typography variant="body2" fontFamily="monospace">
                                  {inv.invoiceNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDate(inv.issuedAt)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {inv.description ?? '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {money(inv.billedTotal)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {money(inv.clientOwes)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {((inv.lineItems && inv.lineItems.length > 0) || inv.tax > 0) && (
                              <TableRow>
                                <TableCell colSpan={5} sx={{ py: 0.75, pl: 5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                                  {(inv.lineItems ?? []).map((li, i) => (
                                    <Box
                                      key={i}
                                      sx={{ display: 'flex', justifyContent: 'space-between', py: 0.2 }}
                                    >
                                      <Typography variant="caption" color="text.secondary">
                                        {li.description}
                                        {li.quantity != null && li.quantity !== 1 ? ` ×${formatQuantity(li.quantity)}` : ''}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {money(li.total)}
                                      </Typography>
                                    </Box>
                                  ))}
                                  {inv.tax > 0 && (
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'baseline',
                                        gap: 3,
                                        mt: 0.5,
                                        pt: 0.5,
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      <Typography variant="caption" color="text.secondary">
                                        Tax
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'right' }}>
                                        {money(inv.tax)}
                                      </Typography>
                                    </Box>
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        ))}
                        {/* Horse subtotal row */}
                        <TableRow key={`subtotal-${group.horseName}`}>
                          <TableCell colSpan={4} align="right">
                            <Typography variant="body2" color="text.secondary">
                              {group.horseName} subtotal
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {money(horseSubtotal)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        </Fragment>
                      );
                    })}

                    {/* Grand total */}
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ borderTop: '2px solid', borderColor: 'divider', p: 0 }}
                      />
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3} />
                      <TableCell align="right">
                        <Typography fontWeight={700}>Grand Total</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color="#8B4513" fontSize={16}>
                          {money(total)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={3}
                textAlign="center"
              >
                Payment due upon receipt
              </Typography>
            </Paper>

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                startIcon={<PrintIcon />}
                variant="outlined"
                onClick={handlePrintStatement}
              >
                Print
              </Button>
              <Button
                startIcon={emailSending ? <CircularProgress size={16} /> : <EmailIcon />}
                variant="outlined"
                onClick={handleEmailInvoice}
                disabled={!ci || emailSending}
              >
                Email to Client
              </Button>
              <Button
                startIcon={<PaymentIcon />}
                variant="contained"
                onClick={() => setStep(2)}
                disabled={!ci}
                sx={{
                  ml: 'auto',
                  bgcolor: '#8B4513',
                  '&:hover': { bgcolor: '#6B3410' },
                }}
              >
                Collect Payment ({money(total)})
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 3 — Collect Payment
  // ─────────────────────────────────────────────────────────────────────────

  function Step3() {
    // Manual record = anything that isn't payment-link or a Stripe card charge.
    const isManual = paymentMethod !== 'payment_link' && !useStripeFlow;
    const canSubmit =
      !confirmingPayment &&
      (paymentMethod === 'payment_link' ||
        (useStripeFlow && stripeMode === 'on_file' && !!(selectedCardId || defaultCard)) ||
        // `!!paymentMethod` is the whole of the loaded-empty block: with no
        // active clinic type the select has no selection, so a manual record
        // can't be submitted (BulkMarkPaid's `!paymentType` pattern).
        (isManual && !!paymentMethod && parseFloat(paymentAmount) > 0));

    const actionLabel =
      paymentMethod === 'payment_link'
        ? 'Send Payment Link'
        : useStripeFlow && stripeMode === 'on_file'
          ? `Charge ${money(invoiceTotal)}`
          : 'Record Payment';

    // The New Card flow has its own Charge button inside the Stripe form.
    const showBottomAction = !(useStripeFlow && stripeMode === 'new_card');

    const stripeTabSx = (active: boolean) =>
      active
        ? { bgcolor: '#8B4513', color: '#fff', textTransform: 'none', '&:hover': { bgcolor: '#6B3410' } }
        : { color: '#8B4513', borderColor: '#8B4513', textTransform: 'none' };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Header summary */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Client
            </Typography>
            <Typography fontWeight={600}>{selectedClient?.name}</Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="overline" color="text.secondary">
              Balance Due
            </Typography>
            <Typography fontWeight={700} fontSize={20} color="#8B4513">
              {money(invoiceTotal)}
            </Typography>
          </Box>
        </Box>

        {/* Payment method (mirrors the invoice payment dialog) */}
        <FormControl fullWidth size="small">
          <InputLabel id="stmt-method-label">Payment Method</InputLabel>
          <Select
            labelId="stmt-method-label"
            label="Payment Method"
            value={paymentMethod}
            onChange={(e) => {
              setMethodChoice(e.target.value);
              setPaymentError(null);
            }}
          >
            {methodOptions.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Loaded and empty: nothing recordable to offer, so say why instead of
            listing built-ins the server would reject on save. */}
        {noRecordableMethods && (
          <Alert severity="warning">
            This clinic has no active payment types. Add or reactivate one under Settings →
            Invoicing before recording a payment.
          </Alert>
        )}

        {/* Credit / Debit Card via Stripe — New Card / On File (like the invoice) */}
        {useStripeFlow && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <Button
                size="small"
                variant={stripeMode === 'new_card' ? 'contained' : 'outlined'}
                onClick={() => { setStripeMode('new_card'); setPaymentError(null); }}
                sx={stripeTabSx(stripeMode === 'new_card')}
              >
                New Card / Bank
              </Button>
              {savedCards.length > 0 && (
                <Button
                  size="small"
                  variant={stripeMode === 'on_file' ? 'contained' : 'outlined'}
                  onClick={() => { setStripeMode('on_file'); setPaymentError(null); }}
                  sx={stripeTabSx(stripeMode === 'on_file')}
                >
                  On File
                </Button>
              )}
            </Box>

            {stripeMode === null && (
              <Typography variant="body2" color="text.secondary">
                Choose a new card/bank or a saved card to charge {money(invoiceTotal)}.
              </Typography>
            )}

            {stripeMode === 'new_card' && (
              cardLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : stripeClientSecret && stripePromise ? (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: stripeClientSecret,
                    appearance: { theme: 'stripe', variables: { colorPrimary: '#8B4513', fontFamily: 'Roboto, sans-serif' } },
                  }}
                >
                  <StatementCardForm
                    amountLabel={money(invoiceTotal)}
                    onSuccess={handleCardSuccess}
                    onCancel={() => setStripeMode(null)}
                  />
                </Elements>
              ) : null
            )}

            {stripeMode === 'on_file' && savedCards.length > 0 && (
              <Paper variant="outlined" sx={{ px: 1.5, py: 0.5 }}>
                <RadioGroup value={selectedCardId} onChange={(e) => setSelectedCardId(e.target.value)}>
                  {savedCards.map((c) => (
                    <FormControlLabel
                      key={c.id}
                      value={c.id}
                      control={<Radio size="small" sx={{ '&.Mui-checked': { color: '#8B4513' } }} />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{formatCard(c)}</Typography>
                          {c.isDefault && (
                            <Chip label="Default" size="small" sx={{ height: 18, fontSize: 11 }} />
                          )}
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </Paper>
            )}
          </Box>
        )}

        {/* Manual record — cash/check/other clinic types (or credit/debit when Stripe is off) */}
        {isManual && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField
              label="Amount"
              size="small"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ width: 180 }}
            />
            <TextField
              label={paymentMethod === 'check' ? 'Check number' : 'Reference (optional)'}
              size="small"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              sx={{ flex: 1, minWidth: 180 }}
            />
          </Box>
        )}

        {/* Payment link — info */}
        {paymentMethod === 'payment_link' && (
          <Alert severity="info">
            Send the client a secure Stripe link for {money(invoiceTotal)}. They can pay by card or bank.
          </Alert>
        )}

        {paymentError && <Alert severity="error">{paymentError}</Alert>}

        {/* Action (the New Card flow has its own Charge button) */}
        {showBottomAction && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
            <Button
              variant="contained"
              disabled={!canSubmit}
              onClick={handleSubmitPayment}
              startIcon={
                confirmingPayment ? <CircularProgress size={16} color="inherit" /> : undefined
              }
              sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6B3410' } }}
            >
              {actionLabel}
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // STEP 4 — Confirm
  // ─────────────────────────────────────────────────────────────────────────

  function Step4() {
    const ci = clientInvoice;
    const total = ci ? Number(ci.totalAmount) : selectedTotal;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, pt: 2 }}>
        {/* Success icon + heading */}
        <CheckCircleIcon sx={{ fontSize: 72, color: 'success.main' }} />
        <Typography variant="h5" fontWeight={700} color="success.dark">
          Payment Confirmed
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Total collected
          </Typography>
          <Typography variant="h4" fontWeight={700} color="#8B4513">
            {money(total)}
          </Typography>
          {ci && (
            <Typography variant="body2" color="text.secondary" fontFamily="monospace" mt={0.5}>
              {ci.invoiceNumber}
            </Typography>
          )}
        </Box>

        <Divider sx={{ width: '100%' }} />

        {/* Source invoices by horse */}
        <Box sx={{ width: '100%' }}>
          <Typography variant="overline" color="text.secondary" display="block" mb={1}>
            Invoices Included
          </Typography>
          {selectedGroupedByHorse.map((group) => (
            <Paper key={group.horseName} variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  {group.horseName}
                </Typography>
                {group.ownershipPercentage != null && (
                  <Chip
                    label={`${group.ownershipPercentage}% ownership`}
                    size="small"
                    sx={{ bgcolor: '#CD853F', color: '#fff', fontSize: 10, height: 18 }}
                  />
                )}
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell align="right">Client Owes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.invoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {inv.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(inv.issuedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {inv.description ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {money(inv.clientOwes)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ))}
        </Box>

        {/* Payment breakdown */}
        <Box sx={{ width: '100%' }}>
          <Typography variant="overline" color="text.secondary" display="block" mb={1}>
            Payment Breakdown
          </Typography>
          <Paper variant="outlined">
            {(clientInvoice?.payments ?? []).map((p, idx, arr) => (
              <Box
                key={p.id ?? idx}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 2,
                  py: 1,
                  borderBottom: idx < arr.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2">
                  {methodLabel(p.method)}
                  {p.referenceNote ? ` · ${p.referenceNote}` : ''}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {money(Number(p.amount))}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                px: 2,
                py: 1.25,
                borderTop: '2px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
              }}
            >
              <Typography fontWeight={700}>Total</Typography>
              <Typography fontWeight={700} color="#8B4513">
                {money(total)}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center', pb: 1 }}>
          <Button
            startIcon={<PrintIcon />}
            variant="outlined"
            onClick={handlePrintStatement}
          >
            Print Receipt
          </Button>
          <Button
            startIcon={<EmailIcon />}
            variant="outlined"
            onClick={handleEmailReceipt}
          >
            Email Receipt
          </Button>
          <Button
            variant="contained"
            onClick={handleDone}
            sx={{ bgcolor: '#8B4513', '&:hover': { bgcolor: '#6B3410' } }}
          >
            Done
          </Button>
        </Box>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Dialog chrome
  // ─────────────────────────────────────────────────────────────────────────

  // In manage mode the create-only steps (Select / Review) don't apply, so never
  // step back below Collect Payment.
  const canGoBack = step > 0 && step < 3 && !(manageMode && step <= 2);

  function handleBack() {
    // Leaving Review does NOT discard the draft — it is keyed on the invoice
    // selection and is reused when the same set comes back (S5 ROOT 3).
    if (step === 2) {
      setPaymentAmount('');
      setPaymentReference('');
      setPaymentError(null);
    }
    setStep((s) => s - 1);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Statement
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Stepper */}
      <Box sx={{ px: 3, pt: 2, pb: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>{stepContent()}</DialogContent>

      {/* Dialog actions — shown on steps 1 and 2 only (step 0 has its own sticky footer, step 3 has inline buttons) */}
      {(step === 1 || step === 2) && (
        <DialogActions
          sx={{
            px: 3,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {canGoBack && (
            // Dead while a draft is being created. Step 0 is the only place the
            // selection can change, so leaving Back live here let the user reach
            // it mid-create: the effect then re-ran, found `creating`, minted
            // nothing, and the in-flight statement for the OLD set landed over
            // the new rows — emailable and payable from there.
            <Button
              onClick={handleBack}
              variant="outlined"
              disabled={creating}
              sx={{ mr: 'auto' }}
            >
              ← Back
            </Button>
          )}
        </DialogActions>
      )}
      {step === 0 && canGoBack && (
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleBack} variant="outlined" sx={{ mr: 'auto' }}>
            ← Back
          </Button>
        </DialogActions>
      )}
      {clientInvoice && (
        <SendPaymentLinkDialog
          open={linkDialogOpen}
          onClose={() => {
            setLinkDialogOpen(false);
            if (linkSent) handleDone();
          }}
          onSuccess={() => setLinkSent(true)}
          statement={{
            id: clientInvoice.id,
            invoiceNumber: clientInvoice.invoiceNumber,
            clientName: clientInvoice.client?.name ?? selectedClient?.name,
            clientEmail: clientInvoice.client?.email ?? selectedClient?.email,
            amountDue: invoiceTotal,
          }}
        />
      )}
    </Dialog>
  );
};

export default ClientInvoiceWizard;
